import { useState, useEffect, useMemo } from "react";
import api from "api";

/**
 * Extrai o nome do arquivo de uma URL
 * @param {string} url - URL do arquivo
 * @returns {string} Nome do arquivo
 */
function extractFileName(url) {
  return url.substring(url.lastIndexOf("/") + 1);
}

/**
 * Converte coordenadas no formato DMS (graus, minutos, segundos) para DD (graus decimais)
 * @param {number} value - Valor da coordenada
 * @param {string} ref - Referência (N, S, E, W)
 * @returns {number} Coordenada em graus decimais
 */
function convertDMSToDD(value, ref) {
  return ref === "S" || ref === "W" ? -Math.abs(value) : Math.abs(value);
}

/**
 * Hook personalizado para carregar imagens de um projeto com cache
 * @param {Object} params - Parâmetros de configuração
 * @param {string} params.email - Email do usuário
 * @param {string} params.name - Nome do projeto
 * @param {string} params.date - Data do projeto
 * @param {string} params.token - Token de autenticação
 * @param {string} params.folder_path - Caminho da pasta do projeto
 * @returns {Object} Objeto contendo as imagens e estado de carregamento
 */
const useImageLoader = params => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isPublic = !params.email && params.token && !params.folder_path;
  const cacheKey = useMemo(
    () =>
      isPublic
        ? `public_images_${params.token}`
        : `project_images_${params.email}_${params.name}_${params.date}_${params.folder_path}`,
    [
      isPublic,
      params.date,
      params.email,
      params.folder_path,
      params.name,
      params.token
    ]
  );

  useEffect(() => {
    const abortController = new AbortController();

    const fetchImages = async () => {
      setLoading(true);
      setError(null);

      try {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          setImages(JSON.parse(cachedData));
          setLoading(false);
          return;
        }

        let response;
        if (isPublic) {
          response = await api.get(
            `${process.env.REACT_APP_API_URL}/projects/shared/${params.token}`,
            { signal: abortController.signal }
          );
        } else {
          response = await api.post(
            "/projects/list_files",
            { folder_path: params.folder_path },
            {
              headers: { Authorization: `Bearer ${params.token}` },
              signal: abortController.signal
            }
          );
        }

        const imageList = response.data.files.map(file => ({
          src: file.url,
          fileName: extractFileName(file.url),
          ...(file.gps && {
            latitude: convertDMSToDD(file.gps.latitude, file.gps.latitude_ref),
            longitude: convertDMSToDD(
              file.gps.longitude,
              file.gps.longitude_ref
            )
          }),
          ...(file.metadata && { metadata: file.metadata })
        }));

        setImages(imageList);
        localStorage.setItem(cacheKey, JSON.stringify(imageList));
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Erro ao carregar imagens:", err);
          setError(err.message || "Erro ao carregar imagens");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchImages();

    return () => abortController.abort();
  }, [cacheKey, isPublic, params.folder_path, params.token]);

  return { images, loading, error };
};

export default useImageLoader;
